/**
 * CapsuleOrderDialog — graduate an ENTIRE capsule into one purchase order.
 *
 * The single-record sibling is AddToPurchaseOrderDialog; this mirrors its
 * structure, conventions, and target section, scaled up to N records:
 *   - A review table: one row per record in the capsule, showing the buy-plan
 *     total (sum of size_quantities across its variants) and an editable unit
 *     price + currency. Records with no buy plan are greyed, labelled "No buy
 *     plan", and excluded from the order.
 *   - A mixed-supplier warning when included records span more than one
 *     supplier (the PO goes to the single supplier chosen below).
 *   - The same target section as AddToPurchaseOrderDialog: a new PO (location +
 *     supplier) or an append to an existing open PO.
 *
 * The server explodes each record's persisted `size_quantities` into PO lines
 * (never client-trusted), so `items` carries only price + currency per record.
 * Single Dialog, never modal-from-modal (DESIGN.md §10).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Loader2, ShoppingCart, Users } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { STATUS_LABELS } from '@/features/purchase-orders/utils/status-style';
import { useAddCapsuleToPurchaseOrder } from '../utils/pipeline-procedures';

/** PO statuses that can still receive new lines (mirrors AddToPurchaseOrderDialog). */
const APPENDABLE_STATUSES = [
  'draft',
  'submitted',
  'in_production',
  'ready',
  'partially_received',
] as const;

const CURRENCY_OPTIONS = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'] as const;

type Mode = 'new' | 'existing';

export interface CapsuleRecord {
  id: string;
  style_name: string;
  style_code: string | null;
  supplier_id: string | null;
  supplier_title: string | null;
  agreed_price: number | null;
  agreed_currency: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  capsule: string;
  records: CapsuleRecord[];
}

interface RowInput {
  price: string;
  currency: string;
}

export function CapsuleOrderDialog({ open, onOpenChange, capsule, records }: Props) {
  const { t } = useTranslation('productDev');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { locations } = useLocations();
  const { suppliers } = useSuppliers();
  const orgCurrency = organization?.default_currency ?? 'USD';

  const addCapsule = useAddCapsuleToPurchaseOrder();
  const submitting = addCapsule.isPending;

  // Buy-plan totals — one variants query per record (server explodes them for
  // real; here we only need the totals to review + to exclude empty records).
  const variantQueries = trpc.useQueries((tq) =>
    records.map((r) =>
      tq.productDev.listVariants(
        { productDevId: r.id },
        { enabled: open, staleTime: 30_000 },
      ),
    ),
  );

  const planByRecord = useMemo(
    () =>
      records.map((_r, i) => {
        const q = variantQueries[i];
        const vs = q?.data ?? [];
        let total = 0;
        for (const v of vs) {
          for (const size of v.sizes) total += v.size_quantities?.[size] ?? 0;
        }
        return { total, loading: q?.isLoading ?? false };
      }),
    [records, variantQueries],
  );
  const anyPlanLoading = planByRecord.some((p) => p.loading);

  // Stable key for seeding row state — new arrays render-to-render otherwise.
  const recordKey = useMemo(() => records.map((r) => r.id).join(','), [records]);

  // Per-row editable price + currency. Seed missing rows from the record's
  // agreed price/currency when the dialog opens; keep edits across re-renders.
  const [rows, setRows] = useState<Record<string, RowInput>>({});
  useEffect(() => {
    if (!open) return;
    setRows((prev) => {
      const next = { ...prev };
      for (const r of records) {
        if (!next[r.id]) {
          next[r.id] = {
            price: r.agreed_price == null ? '' : String(r.agreed_price),
            currency: r.agreed_currency ?? orgCurrency,
          };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordKey, orgCurrency]);

  const setRow = (id: string, patch: Partial<RowInput>) =>
    setRows((prev) => ({
      ...prev,
      [id]: { price: '', currency: orgCurrency, ...prev[id], ...patch },
    }));

  const [mode, setMode] = useState<Mode>('new');
  const [locationId, setLocationId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [poId, setPoId] = useState('');
  const [poPickerOpen, setPoPickerOpen] = useState(false);

  // Included = has a buy plan. Records without one are shown greyed + excluded.
  const included = records.filter((_r, i) => planByRecord[i]!.total > 0);
  const includedCount = included.length;
  const totalUnits = planByRecord.reduce((acc, p) => acc + p.total, 0);

  // Default the new-PO supplier to the most common supplier among included rows.
  useEffect(() => {
    if (!open || supplierId !== '' || includedCount === 0) return;
    const counts = new Map<string, number>();
    for (const r of included) {
      if (r.supplier_id) counts.set(r.supplier_id, (counts.get(r.supplier_id) ?? 0) + 1);
    }
    let best: string | null = null;
    let bestN = 0;
    for (const [sid, n] of counts) {
      if (n > bestN) {
        best = sid;
        bestN = n;
      }
    }
    if (best) setSupplierId(best);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordKey, includedCount]);

  const mixedSuppliers = useMemo(() => {
    const ids = new Set(included.map((r) => r.supplier_id ?? '∅'));
    return ids.size > 1;
  }, [included]);

  // Existing-PO options: any appendable PO (a capsule can span suppliers, so we
  // don't pre-filter by one supplier the way the single-record dialog does).
  const ordersQuery = trpc.purchaseOrders.getOrders.useQuery(
    {},
    { enabled: open && mode === 'existing', staleTime: 60_000, refetchOnWindowFocus: false },
  );
  const poOptions = useMemo(() => {
    const list = ordersQuery.data?.data ?? [];
    return list
      .filter((po) => (APPENDABLE_STATUSES as readonly string[]).includes(po.status))
      .map((po) => ({
        id: po.id,
        label: po.po_number ?? po.title ?? po.id.slice(0, 8),
        status: po.status,
        supplierTitle: po.supplier_name ?? null,
      }));
  }, [ordersQuery.data]);
  const selectedPo = poOptions.find((po) => po.id === poId) ?? null;

  // Validation (live). Every included row needs a valid price; the target must
  // be complete for the chosen mode; at least one record must be orderable.
  const priceOf = (id: string) => Number(rows[id]?.price ?? '');
  const rowPriceValid = (id: string) => {
    const raw = rows[id]?.price ?? '';
    const n = Number(raw);
    return raw.trim() !== '' && Number.isFinite(n) && n >= 0;
  };
  const allPricesValid = included.every((r) => rowPriceValid(r.id));
  const locationValid = mode === 'new' ? locationId !== '' : true;
  const supplierValid = mode === 'new' ? supplierId !== '' : true;
  const poValid = mode === 'existing' ? poId !== '' : true;
  const canSubmit =
    includedCount > 0 &&
    !anyPlanLoading &&
    allPricesValid &&
    locationValid &&
    supplierValid &&
    poValid;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    try {
      const items = included.map((r) => ({
        productDevId: r.id,
        unitPrice: priceOf(r.id),
        currency: rows[r.id]?.currency ?? orgCurrency,
      }));
      const target =
        mode === 'new'
          ? ({ mode: 'new', locationId, supplierId: supplierId || null } as const)
          : ({ mode: 'existing', poId } as const);
      const result = await addCapsule.mutateAsync({ capsule, items, target });
      toast({
        title: t('capsuleOrder.toast.added'),
        description: t('capsuleOrder.toast.addedDesc', {
          lines: result.lineCount,
          records: result.recordCount,
        }),
      });
      onOpenChange(false);
      navigate(`/orders/${result.poId}`);
    } catch (err) {
      toast({
        title: t('capsuleOrder.toast.addFailed'),
        description: err instanceof Error ? err.message : t('capsuleOrder.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (submitting ? undefined : onOpenChange(next))}>
      <DialogContent
        className="sm:max-w-2xl"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void handleSubmit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {t('capsuleOrder.title')}
            <span className="inline-flex items-center rounded-full bg-threadheads-purple/10 px-2 py-0.5 text-[11px] font-medium text-threadheads-purple">
              {capsule}
            </span>
          </DialogTitle>
          <DialogDescription>
            {t('capsuleOrder.description', { count: includedCount, units: totalUnits })}
          </DialogDescription>
        </DialogHeader>

        {mixedSuppliers && (
          <Alert className="border-amber-300 bg-amber-50 text-amber-800 [&>svg]:text-amber-600">
            <Users className="h-4 w-4" />
            <AlertDescription className="text-amber-800">
              {t('capsuleOrder.mixedSuppliers')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Review table */}
          <div className="rounded-md border">
            <div className="grid grid-cols-[1fr_auto_9rem] items-center gap-3 border-b bg-muted/40 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <span>{t('capsuleOrder.col.style')}</span>
              <span className="text-right tabular-nums">{t('capsuleOrder.col.units')}</span>
              <span>{t('capsuleOrder.col.unitPrice')}</span>
            </div>
            <div className="max-h-72 divide-y overflow-y-auto">
              {records.map((r, i) => {
                const plan = planByRecord[i]!;
                const hasPlan = plan.total > 0;
                const row = rows[r.id] ?? { price: '', currency: orgCurrency };
                const priceError = hasPlan && !rowPriceValid(r.id) && row.price.trim() !== '';
                return (
                  <div
                    key={r.id}
                    className={cn(
                      'grid grid-cols-[1fr_auto_9rem] items-center gap-3 px-3 py-2',
                      !hasPlan && 'opacity-55',
                    )}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{r.style_name}</div>
                      <div className="flex items-center gap-1.5">
                        {r.style_code && (
                          <span className="truncate font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                            {r.style_code}
                          </span>
                        )}
                        {!hasPlan && !plan.loading && (
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {t('capsuleOrder.noBuyPlan')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm tabular-nums">
                      {plan.loading ? (
                        <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      ) : hasPlan ? (
                        <span className="font-medium">{plan.total}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                    {hasPlan ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          aria-label={t('capsuleOrder.priceAria', { name: r.style_name })}
                          value={row.price}
                          onChange={(e) => setRow(r.id, { price: e.target.value })}
                          className={cn(
                            'h-8 w-[5.5rem] text-sm tabular-nums',
                            priceError && 'border-destructive ring-1 ring-destructive/30',
                          )}
                        />
                        <Select
                          value={row.currency}
                          onValueChange={(v) => setRow(r.id, { currency: v })}
                        >
                          <SelectTrigger className="h-8 w-[4.25rem] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCY_OPTIONS.map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <span className="text-right text-xs text-muted-foreground">-</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {includedCount === 0 && !anyPlanLoading && (
            <p className="text-sm text-muted-foreground">{t('capsuleOrder.noneOrderable')}</p>
          )}
          {included.some((r) => !rowPriceValid(r.id)) && (
            <p className="text-sm text-destructive">{t('capsuleOrder.errPrices')}</p>
          )}

          {/* Target — mode */}
          <div className="space-y-2">
            <Label className="text-xs">{t('capsuleOrder.purchaseOrder')}</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as Mode)}
              className="grid grid-cols-2 gap-2"
            >
              <label
                htmlFor="cap-mode-new"
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                  mode === 'new'
                    ? 'border-threadheads-purple/50 bg-threadheads-purple/5'
                    : 'border-border hover:bg-muted/40',
                )}
              >
                <RadioGroupItem value="new" id="cap-mode-new" />
                {t('capsuleOrder.newPo')}
              </label>
              <label
                htmlFor="cap-mode-existing"
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                  mode === 'existing'
                    ? 'border-threadheads-purple/50 bg-threadheads-purple/5'
                    : 'border-border hover:bg-muted/40',
                )}
              >
                <RadioGroupItem value="existing" id="cap-mode-existing" />
                {t('capsuleOrder.existingPo')}
              </label>
            </RadioGroup>
          </div>

          {/* New mode: supplier + location */}
          {mode === 'new' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="cap-supplier" className="text-xs">
                  {t('capsuleOrder.supplier')}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger
                    id="cap-supplier"
                    className={cn(supplierId === '' && 'border-amber-300 ring-1 ring-amber-100/50')}
                  >
                    <SelectValue placeholder={t('capsuleOrder.chooseSupplier')} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {supplierId === '' && (
                  <p className="text-xs text-amber-700">{t('capsuleOrder.chooseSupplierHint')}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="cap-location" className="text-xs">
                  {t('capsuleOrder.location')}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger
                    id="cap-location"
                    className={cn(locationId === '' && 'border-amber-300 ring-1 ring-amber-100/50')}
                  >
                    <SelectValue placeholder={t('capsuleOrder.chooseLocation')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locationId === '' && (
                  <p className="text-xs text-amber-700">{t('capsuleOrder.chooseLocationHint')}</p>
                )}
              </div>
            </div>
          )}

          {/* Existing mode: searchable PO combobox */}
          {mode === 'existing' && (
            <div className="space-y-1">
              <Label className="text-xs">
                {t('capsuleOrder.purchaseOrder')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Popover open={poPickerOpen} onOpenChange={setPoPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={poPickerOpen}
                    className={cn(
                      'w-full justify-between font-normal',
                      poId === '' && 'border-amber-300 text-muted-foreground ring-1 ring-amber-100/50',
                    )}
                    disabled={ordersQuery.isLoading}
                  >
                    {ordersQuery.isLoading ? (
                      <span className="inline-flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('capsuleOrder.loadingOrders')}
                      </span>
                    ) : selectedPo ? (
                      <span className="inline-flex items-center gap-2 truncate">
                        <span className="truncate">{selectedPo.label}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {STATUS_LABELS[selectedPo.status] ?? selectedPo.status}
                        </span>
                      </span>
                    ) : (
                      t('capsuleOrder.choosePo')
                    )}
                    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('capsuleOrder.searchOrders')} />
                    <CommandList>
                      <CommandEmpty>{t('capsuleOrder.noOpenPos')}</CommandEmpty>
                      {poOptions.map((po) => (
                        <CommandItem
                          key={po.id}
                          value={`${po.label} ${po.supplierTitle ?? ''} ${po.status}`}
                          onSelect={() => {
                            setPoId(po.id);
                            setPoPickerOpen(false);
                          }}
                          className="gap-2"
                        >
                          <Check
                            className={cn(
                              'h-3.5 w-3.5 shrink-0',
                              po.id === poId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="min-w-0 flex-1 truncate">{po.label}</span>
                          {po.supplierTitle && (
                            <span className="shrink-0 truncate text-xs text-muted-foreground">
                              {po.supplierTitle}
                            </span>
                          )}
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {STATUS_LABELS[po.status] ?? po.status}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {poId === '' && (
                <p className="text-xs text-amber-700">{t('capsuleOrder.choosePoHint')}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('capsuleOrder.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || submitting}
            className="h-9 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {t('capsuleOrder.adding')}
              </>
            ) : (
              <>
                <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />{' '}
                {t('capsuleOrder.submit', { count: includedCount })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
